import { ContentTrackContextProvider } from '@src/components/sco/Education/ContentTrackContext';
import { ActiveContentContextValue, BreadcrumbItem, SiteSection, useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { Term } from '@src/graphql-types';
import { useUniversalState } from '@src/hooks/useUniversalState';
import { usePathname } from 'next/navigation';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
type EducationContextValue = {
  activeContent?: ActiveContentContextValue;
  activeKeywords?: string;
  activeSection?: string;
  activeSectionParts?: string[];
  activeTerms?: Term[];
  activeTermsSlugs?: string[];
  setActiveContentItem?: (item: any) => void;
  setActiveKeywords?: (keywords: string) => void;
  setActiveSection?: (section: SiteSection) => void;
  setActiveTerms?: (topic: Term[]) => void;
  setBreadcrumbs?: (breadcrumbs: BreadcrumbItem[]) => void;
  children?: ReactNode;
};
export const EducationContext = createContext<EducationContextValue>({});
export const useEducationContext = () => {
  return useContext(EducationContext);
};
export const EducationContextProvider: FC<EducationContextValue> = props => {
  const activeContent = useActiveContentContext();
  const pathname = usePathname();
  const [activeTerms, setActiveTerms] = useUniversalState<Term[]>('EducationContext.activeTerms', props.activeTerms || []);
  const [activeKeywords, setActiveKeywords] = useQueryState('keywords', parseAsString.withDefault(props.activeKeywords || null));
  const [activeTermsSlugs, setActiveTermsSlugs] = useQueryState('terms', parseAsArrayOf(parseAsString).withDefault([]));

  // Update URL from activeTerms
  useEffect(() => {
    if (activeTerms?.length > 0) {
      setActiveTermsSlugs(activeTerms.map(t => t.slug));
    } else {
      setActiveTermsSlugs(null);
    }
  }, [activeTerms]);

  // Push URL to activeTerms
  useEffect(() => {
    if (activeTerms.length == 0 && activeTermsSlugs?.length > 0) {
      const terms = activeTermsSlugs.map(t_0 => t_0);
      setActiveTerms(activeTermsSlugs.map(slug => ({
        name: slug,
        slug: slug
      }) as Term));
    }
  }, []);
  const activeSection = useMemo(() => activeContent.siteSection, [activeContent.siteSection]);
  const activeSectionParts = useMemo(() => activeContent.siteSectionParts, [activeContent.siteSection]);
  const setBreadcrumbs = useCallback(breadcrumbs => activeContent.setBreadcrumbs(breadcrumbs), []);
  const setActiveContentItem = useCallback(item => activeContent.setItem(item), []);
  const setActiveSection = useCallback(section => activeContent.setSiteSection(section), []);
  return <EducationContext.Provider value={{
    activeContent,
    activeKeywords,
    activeSection,
    activeSectionParts,
    activeTerms,
    activeTermsSlugs,
    setActiveContentItem,
    setActiveKeywords,
    setActiveSection,
    setActiveTerms,
    setBreadcrumbs
  }}>
      <ContentTrackContextProvider>{props.children}</ContentTrackContextProvider>
    </EducationContext.Provider>;
};